import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { CustomTextField } from "../../../../../shared/components/inputfields/form-elements";
import { NavLink, useParams } from "react-router-dom";
import useNotification from "../../../../../shared/hooks/useNotification";
import { useApi } from "../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import { Utills } from "../../../../../shared/app-configs/utilit";
import { InnerButton } from "../../../../../shared/components/buttons/floating-button-container";
import { useTransactionCertificateStore, useFormStore } from "../../../../../store/transaction-certificates.store";
import { useTcTabErrorStore } from "../../../../../store/tcTabErrors.store";
import ViewerModalPopup from "../../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../../shared/components/preview/pdf-viewer";
import { downloadDocx, DownloadDocxOptions } from "../../../../../shared/components/document/docx-downloader";
import { declarationByCertificateBodyService } from "../../tc-service/declaration-by-certificate-body.service";
import { IDeclarationSubmitReqDto } from "../../tc-models/tc-req-dto/declaration-by-certificate-body.req_dto";
import { ErrorHandler } from "../../../../../shared/utils/error-handler.utils";
import {
  friendlyTcV4SubmitError,
  translateTcV4ValidationDetails,
} from "../../../../../shared/utils/tc-v4-validation-messages.utils";


const DeclarationByCertificateBody: React.FC<{
  onNextTab: any;
  completionMap: Record<string, boolean>;
}> = ({ onNextTab, completionMap }) => {
  const [outsourced, setOutsourced] = useState<string>("N");
  const [additionalText, setAdditionalText] = useState<string>("");
  const [additionalTextError, setAdditionalTextError] = useState("");
  const [showDownload, setShowDownload] = useState(false);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeclarationSaved, setIsDeclarationSaved] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [submitValidationMessages, setSubmitValidationMessages] = useState<string[]>([]);

  const { notify } = useNotification();
  const { TE_ID, formtype } = useParams<string>();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  const { executePost, executeGet, loading: pageLoading } = useApi();
  const { cbLicensingCode } = useTransactionCertificateStore();

  // Modal handlers
  const togglePreviewDrawer = () => setPreviewOpen((prev) => !prev);

  // Fetch TC Standard & Existing Declaration
  useEffect(() => {
    if (!TE_ID) return;
    setIsDeclarationSaved(false);
    setSubmitValidationMessages([]);

    const fetchData = async () => {
      try {
        // Fetch existing declaration values from declaration endpoint
        try {
          const declResp: any = await executeGet(TCAppConfig.GET_DECLARATION_SUBMIT_DETAILS_V4, { tcId: TE_ID });
          const respData = declResp?.data || declResp || {};

          const sellerDecl =
            respData?.tcSellerProductDeclarations ??
            "";

          const outsourcedVal =
            respData?.tcOutsourcedToSubcontractor ??
            "";

          // Transform API response to match radio button values
          let normalizedOutsourced = "";
          if (outsourcedVal) {
            const upperVal = String(outsourcedVal).toUpperCase();
            if (upperVal === "Y" || upperVal === "YES") {
              normalizedOutsourced = "Y";
            } else if (upperVal === "N" || upperVal === "NO") {
              normalizedOutsourced = "N";
            } else {
              normalizedOutsourced = String(outsourcedVal);
            }
          }

          if (sellerDecl || outsourcedVal) {
            setAdditionalText(String(sellerDecl || ""));
            setOutsourced(normalizedOutsourced);
            setShowDownload(false);
            setIsSubmitted(false);
          }
        } catch (e) {
          console.log('Failed to load declaration details:', e);
        }
      } catch (error) {
        notify("Unable to fetch TC or Declaration details", "error");
      }
    };

    fetchData();
  }, [TE_ID]);
  


const handleSubmit = async () => {
  // The seller product declaration is OPTIONAL free text; nothing on this tab
  // is mandatory anymore (86ajcn0z8 — declarations tab content revision).
  setAdditionalTextError("");

  try {
    const payload: IDeclarationSubmitReqDto = {
      tcId: TE_ID ?? "",
      tcSellerProductDeclarations: additionalText.trim(),
      // The outsourced-to-subcontractor flag is no longer captured on this
      // tab; keep sending the stored value (default "N") — the backend model
      // requires the key.
      tcOutsourcedToSubcontractor: outsourced || "N",
    };

    const result = await executePost(TCAppConfig.POST_DECLARATION_SUBMIT_DETAILS_V4, payload);

    if (result?.status === 200) {
      notify("Declaration saved successfully", "success");
      setShowDownload(false);
      setIsSubmitted(false);
      setIsDeclarationSaved(true);
      await onNextTab();
    } else {
      const errorMessage = ErrorHandler.extractErrorMessage(result, "Unable to save declaration");
      notify(errorMessage, "error");
    }

  } catch (error: any) {
    console.error("handleSubmit error:", error);
    const errorMessage = ErrorHandler.extractErrorMessage(error, "Error submitting declaration");
    notify(errorMessage, "error");
  }
};
  
  // Surface field-level validation details from the submit API so the user can
  // act on them without inspecting the network response. Every message is
  // shown — collapsing the tail into "and N more validation issues" hid the
  // remaining reasons from testers (86ajjf3u1). Backend JSON property paths
  // are translated to UI field names — internal paths must never be shown to
  // end users (86ajcn0z8).
  const getSubmitValidationMessages = (source: any, fallback: string): string[] => {
    const details = ErrorHandler.extractValidationDetails(source);
    if (details.length > 0) {
      return Array.from(new Set(translateTcV4ValidationDetails(details)));
    }

    const errorMessage = ErrorHandler.extractErrorMessage(source, fallback);
    return [friendlyTcV4SubmitError(errorMessage)];
  };

  const showSubmitFailure = (source: any, fallback: string) => {
    const messages = getSubmitValidationMessages(source, fallback);
    setSubmitValidationMessages(messages);
    notify(
      messages.length === 1
        ? messages[0]
        : `TC could not be submitted. Review the ${messages.length} issues shown below.`,
      "error"
    );
  };

  // Submit TC
  const handleTCSubmit = async () => {
    if (!TE_ID) {
      notify("TC ID is required", "error");
      return;
    }
    if (!isDeclarationSaved) {
      notify("Save the declaration before submitting the TC", "error");
      return;
    }
    if (isSubmitting) {
      return;
    }

    const { tabErrors } = useTcTabErrorStore.getState();
    const hasPrecedingErrors = Object.entries(tabErrors).some(
      ([tabId, hasError]) => tabId !== "declaration-submit-details" && hasError
    );

    if (hasPrecedingErrors) {
      const message = "Please resolve all validation errors in the highlighted tabs before submitting.";
      setSubmitValidationMessages([message]);
      notify(message, "error");
      return;
    }

    setSubmitValidationMessages([]);
    setIsSubmitting(true);

    try {
      // Call validation API first
      const validationResult = await executeGet(TCAppConfig.GET_TC_COMPONENT_NET_SHIPPING_WEIGHT_V4, {
        tcId: TE_ID,
      });

      // If validation request itself fails, stop here (useApi shows errors)
      if (!validationResult || validationResult.status !== 200) {
        showSubmitFailure(
          validationResult,
          "Unable to validate TC before submit"
        );
        return;
      }

      // Check validation payload for any invalid products
      const rawValidation: any = validationResult?.data ?? validationResult ?? {};
      let validationData: any = rawValidation;

      // Support stringified JSON responses
      if (typeof validationData === "string") {
        try {
          validationData = JSON.parse(validationData);
        } catch (e) {
          console.warn("Failed to parse validation response string", e);
        }
      }

      // Also handle nested data/response shapes
      const products: any[] =
        (Array.isArray(validationData?.products) && validationData.products) ||
        (Array.isArray(validationData?.data?.products) && validationData.data.products) ||
        (Array.isArray(validationData?.response?.products) && validationData.response.products) ||
        [];

      const summary =
        validationData?.summary ||
        validationData?.data?.summary ||
        validationData?.response?.summary ||
        {};
      const invalidProducts = products
        .flatMap((p) => {
          const results: { article: string; msg: string }[] = [];
          const article =
            p?.productDetails?.tcProductArticleNo || p?.productId || "Unknown product";

          // Net weight validation
          const wv = p?.weightValidation;
          if (wv) {
            const remaining = Number(wv.remainingCapacity);
            const hasRemaining =
              !Number.isNaN(remaining) && remaining > 0; // treat remaining capacity as invalid
            const isInvalid = wv.isValid === false || hasRemaining;

            if (isInvalid) {
              const remainingText = Number.isNaN(remaining) ? "0" : remaining.toString();
              const msg = `Net weight mismatch for product article ${article}. ${remainingText} KG of Net weight is missing in components`;
              results.push({ article, msg });
            }
          }

          // Certified weight validation (API field: weightValidationCertified)
          const cv = (p as any)?.weightValidationCertified;
          if (cv) {
            const remaining = Number(cv.remainingCapacity);
            const hasRemaining =
              !Number.isNaN(remaining) && remaining > 0; // treat remaining capacity as invalid
            const isInvalid = cv.isValid === false || hasRemaining;

            if (isInvalid) {
              const remainingText = Number.isNaN(remaining) ? "0" : remaining.toString();
              const msg = `Certified weight mismatch for product article ${article}. ${remainingText} KG of certified weight is missing in components`;
              results.push({ article, msg });
            }
          }

          return results;
        })
        .filter(Boolean) as { article: string; msg: string }[];

      if ((summary?.invalidProducts ?? 0) > 0 || invalidProducts.length > 0) {
        const messages =
          invalidProducts.length > 0
            ? Array.from(new Set(invalidProducts.map((product) => product.msg)))
            : ["Net weight mismatch for one or more products. Net weight is missing in components."];
        setSubmitValidationMessages(messages);
        notify(
          messages.length === 1
            ? messages[0]
            : `TC could not be submitted. Review the ${messages.length} issues shown below.`,
          "error"
        );

        // Do not proceed to TC submit when validation reports errors
        return;
      }

      // Validation passed, proceed with TC submit
      const result = await executePost(TCAppConfig.TC_SUBMIT_DATA_V4, { tcId: TE_ID });
      if (result?.status === 200) {
        notify("TC Submitted Successfully", "success");
        setSubmitValidationMessages([]);
        setIsSubmitted(true);
        setShowDownload(true);
        useFormStore.getState().resetAllPages();
      } else {
        showSubmitFailure(result, "Unable to Submit");
      }
    } catch (error: any) {
      console.error("Failed to submit TC:", error);
      showSubmitFailure(error, "Error submitting TC");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate JSON
  const handleGenerateJSON = async () => {
    try {
      const result = await executeGet(TCAppConfig.GET_DECLARATION_JSON_DETAILS, { tcCbLicensingCode: cbLicensingCode, tcId: TE_ID });
      if (result?.status === 200) {
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: "application/json" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `declaration_${TE_ID}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notify("JSON downloaded successfully", "success");
      } else notify("Failed to generate JSON", "error");
    } catch (error) {
      notify("Error generating JSON", "error");
    }
  };

  // Download Word DOCX
  const handleDownload = async () => {
    setIsDownloading(true);
    const options: DownloadDocxOptions = {
      apiUrl: TCAppConfig.GENERATE_WORD_TC_V4,
      params: {
        tcId: TE_ID ?? "",
        cbCode: cbLicensingCode ?? "",
      },
    };

    try {
      await downloadDocx(options);
    } catch (error) {
      console.error("DOCX download error:", error);
      notify("Failed to download DOCX", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box p={4}>
      {pageLoading ? (
        <LoaderComponent />
      ) : (
        <>
          <Typography variant="body1" fontWeight="600">Declaration by the Certification Body:</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            The Certification Body confirms that all required documentation, certified quantities,
            and scope information have been verified in accordance with TE-TXL-POL-203-V4.0 prior
            to issuing this Transaction Certificate. The products listed have been verified as
            within the valid certification scope of the applicable Textile Exchange standard(s),
            and compliance has been confirmed based on the reviewed certification records.
          </Typography>

          <Typography variant="body1" fontWeight="600" mt={3}>Declaration by the Seller:</Typography>
          <CustomTextField
            label="Seller product declarations"
            variant="outlined"
            fullWidth
            multiline
            readOnly={hasSubmitTc}
            rows={6}
            value={additionalText}
            onChange={(e) => {
              setAdditionalText(e.target.value);
              setAdditionalTextError("");
              setIsDeclarationSaved(false);
              setSubmitValidationMessages([]);
            }}
            error={Boolean(additionalTextError)}
            helperText={additionalTextError}
            sx={{ mt: 1, mb: 4 }}
          />

          {submitValidationMessages.length > 0 && (
            <Alert
              severity="error"
              role="alert"
              data-testid="tc-submit-validation-alert"
              sx={{ mb: 3 }}
            >
              <AlertTitle>TC could not be submitted</AlertTitle>
              <Typography variant="body2">
                Correct the following issue{submitValidationMessages.length === 1 ? "" : "s"} and try again:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                {submitValidationMessages.map((message) => (
                  <Typography component="li" variant="body2" key={message}>
                    {message}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", gap: "0.5rem" }}>
            <NavLink to="/v4/tc-dashboard">
              <InnerButton variant="contained">Home</InnerButton>
            </NavLink>
            
            {!(hasSubmitTc) && (
              <InnerButton variant="contained" onClick={handleSubmit} color="primary">
                Save
              </InnerButton>
            )}
            
            {(hasSubmitTc) && (
              <NavLink to={`/v4/tc/${formtype}/certified-standard-weight/${TE_ID}`}>
                <InnerButton>Back</InnerButton>
              </NavLink>
            )}

            {completionMap["declaration-submit-details"] && (
              <Tooltip
                title={isDeclarationSaved ? "Click to Submit" : "Save the declaration before submitting"}
              >
                <span>
                  <InnerButton
                    onClick={handleTCSubmit}
                    disabled={isSubmitting || !isDeclarationSaved}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </InnerButton>
                </span>
              </Tooltip>
            )}

            {completionMap["declaration-submit-details"] && (
              <Tooltip title={"Click to Preview"}>
                <span>
                  <InnerButton onClick={togglePreviewDrawer}>Preview</InnerButton>
                </span>
              </Tooltip>
            )}

            {(hasSubmitTc ||
              (completionMap["declaration-submit-details"] &&
                (isSubmitted || showDownload))) && (
              <Tooltip title={"Click to Download"}>
                <span>
                  <InnerButton 
                    onClick={handleDownload}
                    disabled={isDownloading}
                    sx={{
                      position: "relative",
                      "& .loading-indicator": {
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                      },
                    }}
                  >
                    {isDownloading ? (
                      <Box className="loading-indicator" display="flex" alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                      </Box>
                    ) : (
                      "Download Word"
                    )}
                  </InnerButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {TE_ID && (
            <ViewerModalPopup open={previewOpen} onClose={togglePreviewDrawer} title="Preview">
              <div style={{ width: "100%", height: "100vh" }}>
                <PdfViewer apiUrl={TCAppConfig.PREVIEW_TC_V4} params={{ tcId: TE_ID }} />
              </div>
            </ViewerModalPopup>
          )}
        </>
      )}
    </Box>
  );
};

export default DeclarationByCertificateBody;
